import { Button, Modal } from 'antd';
import { useStore } from 'zustand';

import { useSaveExperienceBulkEdit } from 'app/hooks/data/core/extranet/experience/useExperienceBulkEdit';
import type {
  ExperienceBulkEditPayload,
  OpeningDays,
} from 'app/typings/core/extranet/experiences/experienceBulkEdit';
import { pluralize } from 'app/utils/strings';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import {
  InventoryTabItemName,
  inventoryStore,
} from '../../../../InventoryStore';
import { formatPackList } from '../_utils/formatPackList';

import styles from './ValidationExperienceBulkEditModal.module.scss';

type Props = {
  isOpened: boolean;
  handleClose: () => void;
  setIsValidationBulkEditModalOpen: (
    isValidationBulkEditModalOpen: boolean
  ) => void;
  hotelId: number;
  payload: ExperienceBulkEditPayload;
  experienceOpeningDays: OpeningDays;
  mustHavePackNames?: string[];
};

export const ValidationExperienceBulkEditModal = ({
  isOpened,
  handleClose,
  setIsValidationBulkEditModalOpen,
  hotelId,
  payload,
  experienceOpeningDays,
  mustHavePackNames = [],
}: Props) => {
  const hasMustHavePacks = mustHavePackNames.length > 0;
  const isSingleMustHavePack = mustHavePackNames.length === 1;
  const timePeriod = useStore(inventoryStore, (state) => state.timePeriod);

  const { mutateAsync: bulkEditSave } = useSaveExperienceBulkEdit(
    hotelId,
    timePeriod
  );

  const handleCancel = () => {
    setIsValidationBulkEditModalOpen(false);
    amplitudeTrack('Return to Bulk Edit Changes', {
      tab: InventoryTabItemName.Experiences,
    });
  };

  const handleSaveChange = async () => {
    await bulkEditSave(payload);

    amplitudeTrack('Accept Bulk Edit Changes', {
      stockPublished: experienceOpeningDays.publishedDayCount,
      stockNotPublished: experienceOpeningDays.unpublishedDayCount,
      nbAutoDiscountDeactivated: 0,
      tab: InventoryTabItemName.Experiences,
    });

    handleClose();
  };

  return (
    <Modal
      className={styles.modal}
      open={isOpened}
      title="Apply your changes?"
      onCancel={handleCancel}
      wrapClassName={styles.validationBulkEditModal}
      footer={[
        <Button size="large" key="back" type="ghost" onClick={handleCancel}>
          Back to edit
        </Button>,
        <Button
          key="save"
          size="large"
          type="primary"
          htmlType="submit"
          onClick={handleSaveChange}
        >
          Save changes
        </Button>,
      ]}
    >
      <p>By saving your changes, this activity will be:</p>
      <ul>
        <li>
          available on{' '}
          <b>
            {pluralize(experienceOpeningDays.publishedDayCount, 'day', 'days')}
          </b>
        </li>
        <li>
          unavailable on{' '}
          <b>
            {pluralize(
              experienceOpeningDays.unpublishedDayCount,
              'day',
              'days'
            )}
          </b>
        </li>
      </ul>
      {hasMustHavePacks && (
        <p>
          This activity is a must-have in {formatPackList(mustHavePackNames)}.
          <br />
          If unavailable,{' '}
          <b>
            {isSingleMustHavePack
              ? 'this package may no longer be available for sale'
              : 'these packages may no longer be available for sale'}
          </b>
          .
        </p>
      )}
    </Modal>
  );
};
